// BALANCO (2026-07-17): balanço patrimonial GERENCIAL + ponte lucro→caixa.
// Consome GET /dre/balanco (dre-engine/handlers/balanco.js). Didático de
// propósito: cada linha explica o que é — o Lucas está usando pra aprender
// a ler o financeiro da empresa. Não substitui o balanço oficial do contador.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Row, Col, Card, Statistic, Select, Button, Alert, Spin, Table, Typography, Tag, Space, Tooltip,
} from 'antd';
import { ReloadOutlined, BankOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Text, Title } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

const brl = (v: number) =>
  (v < 0 ? '−R$ ' : 'R$ ') + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface Balanco {
  mes: string; base: string; data_foto: string;
  ativo: {
    caixa: { total: number; data_snapshot: string | null; origem: string | null; contas: Array<{ conta: string; saldo: number; data: string }> };
    clientes: { qtd: number; total: number };
    imobilizado: { qtd: number; custo: number; liquido: number; categorias: Array<{ categoria: string; tipo: string; qtd: number; custo: number; liquido: number }> };
    mutuo_socios?: { qtd: number; total: number; socios: Array<{ socio: string; emprestado: number; devolvido: number; saldo: number }> };
    despesas_antecipadas?: { qtd: number; total: number; itens: Array<{ nome: string; qtd: number; total: number; saldo: number; periodo_inicio: string; periodo_fim: string }> };
    total: number;
  };
  passivo: {
    fornecedores: { qtd: number; total: number };
    emprestimos: { principal: number; detalhe: Array<{ instituicao: string; sistema: string; parcelas_abertas: number; principal: number; parcelas_futuras: number }> };
    adiantamentos: { total: number; itens: Array<{ descricao: string; periodo: string; valor: number }> };
    total: number;
  };
  patrimonio_liquido: number;
  comparativo_base: { caixa: number; clientes: number; fornecedores: number; adiantamentos: number; despesas_antecipadas?: number };
  info: {
    carteira_a_faturar: { qtd: number; bruto: number; liquido: number };
    compromissos_futuros: { qtd: number; total: number };
    cartao_em_aberto_incluso_no_cp: number;
  };
  ponte: {
    meses: string[]; caixa_inicial: number; caixa_final: number; variacao_real: number;
    passos: Array<{ rotulo: string; valor: number }>;
    variacao_esperada: number; residuo: number;
  };
}

// últimos 12 meses até o corrente, formato YYYY/MM
function mesesDisponiveis(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    out.push(`${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export default function BalancoPatrimonial() {
  const [mes, setMes] = useState(mesesDisponiveis()[0]);
  const [dados, setDados] = useState<Balanco | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/dre/balanco?mes=${encodeURIComponent(mes)}`, {
        headers: { Authorization: token },
      });
      const d = res.data?.body ? JSON.parse(res.data.body) : res.data;
      setDados(d);
    } catch (e: any) {
      setErro(e?.response?.data?.erro || e?.message || 'Falha ao montar o balanço');
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => { carregar(); }, [carregar]);

  const delta = (atual: number, base: number) => {
    const d = atual - base;
    if (Math.abs(d) < 1) return null;
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        {d > 0 ? '+' : '−'}{brl(Math.abs(d)).replace('R$ ', '')} desde {dados?.base}
      </Text>
    );
  };

  const linha = (rotulo: ReactNode, valor: number, extra?: ReactNode, forte = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 13, fontWeight: forte ? 700 : 400 }}>{rotulo}{extra && <div>{extra}</div>}</div>
      <div style={{ fontWeight: forte ? 700 : 500, whiteSpace: 'nowrap' }}>{brl(valor)}</div>
    </div>
  );

  const dica = (txt: string) => (
    <Tooltip title={txt}><InfoCircleOutlined style={{ color: '#94a3b8', marginLeft: 6 }} /></Tooltip>
  );

  return (
    <div style={{ padding: 16 }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <BankOutlined style={{ fontSize: 18, color: '#0ea5e9' }} />
        <Title level={5} style={{ margin: 0 }}>Balanço gerencial</Title>
        <Select value={mes} onChange={setMes} style={{ width: 120 }}
          options={mesesDisponiveis().map(m => ({ value: m, label: m }))} />
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>Atualizar</Button>
      </Space>

      {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 16 }} />}

      <Spin spinning={loading}>
        {dados && (
          <>
            {/* Cards-resumo */}
            <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0f9ff', borderColor: '#bae6fd', height: '100%' }}>
                  <Statistic title="Ativo — o que a empresa TEM" value={dados.ativo.total} precision={0} prefix="R$" />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#fff7ed', borderColor: '#fed7aa', height: '100%' }}>
                  <Statistic title="Passivo — o que ela DEVE" value={dados.passivo.total} precision={0} prefix="R$" />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', height: '100%' }}>
                  <Statistic title="Patrimônio Líquido (a riqueza dos sócios)" value={dados.patrimonio_liquido} precision={0} prefix="R$"
                    valueStyle={{ color: dados.patrimonio_liquido >= 0 ? '#15803d' : '#dc2626' }} />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ height: '100%' }}>
                  <Statistic title={`Caixa e bancos (${dados.ativo.caixa.data_snapshot ?? '—'})`} value={dados.ativo.caixa.total} precision={0} prefix="R$" />
                </Card>
              </Col>
            </Row>

            <Row gutter={24}>
              {/* ATIVO */}
              <Col xs={24} lg={12} style={{ marginBottom: 20 }}>
                <Card size="small" title={`ATIVO — foto de ${dados.data_foto}`}>
                  {linha(<>Caixa e bancos{dica('Saldo consolidado das contas bancárias no snapshot mais próximo da data.')}</>,
                    dados.ativo.caixa.total, delta(dados.ativo.caixa.total, dados.comparativo_base.caixa))}
                  {linha(<>Clientes a receber ({dados.ativo.clientes.qtd} parcelas){dica('Notas já faturadas que os clientes ainda não pagaram. Dinheiro seu, parado na mão deles.')}</>,
                    dados.ativo.clientes.total, delta(dados.ativo.clientes.total, dados.comparativo_base.clientes))}
                  {linha(<>Imobilizado líquido ({dados.ativo.imobilizado.qtd} bens){dica(`Equipamentos, veículos e software: custaram ${brl(dados.ativo.imobilizado.custo)}, valem isso depois do desgaste (depreciação).`)}</>,
                    dados.ativo.imobilizado.liquido)}
                  {/* Estas duas linhas já entravam no Total do Ativo; a de sócios nunca tinha sido
                      mostrada (o total era maior que a soma do que se via). 04/10/2026. */}
                  {!!dados.ativo.mutuo_socios && Math.abs(dados.ativo.mutuo_socios.total) >= 0.5 && linha(
                    <>Empréstimos a sócios (a receber){dica(dados.ativo.mutuo_socios.socios.map(s => `${s.socio}: ${brl(s.saldo)}`).join(' · ')
                      || 'Dinheiro que a empresa emprestou a sócio e ainda não voltou.')}</>,
                    dados.ativo.mutuo_socios.total)}
                  {!!dados.ativo.despesas_antecipadas && Math.abs(dados.ativo.despesas_antecipadas.total) >= 0.5 && linha(
                    <>Despesas pagas antecipadamente ({dados.ativo.despesas_antecipadas.qtd}){dica('Despesas diferidas (Pagamentos → Diferimentos): a parte que ainda vai entrar na DRE nos próximos meses. '
                      + dados.ativo.despesas_antecipadas.itens.map(i => `${i.nome}: ${brl(i.saldo)}`).join(' · '))}</>,
                    dados.ativo.despesas_antecipadas.total,
                    dados.comparativo_base.despesas_antecipadas != null
                      ? delta(dados.ativo.despesas_antecipadas.total, dados.comparativo_base.despesas_antecipadas) : undefined)}
                  {linha('Total do Ativo', dados.ativo.total, undefined, true)}
                  <Table
                    size="small" pagination={false} style={{ marginTop: 12 }} rowKey={(r: any) => r.categoria + r.tipo}
                    title={() => <Text type="secondary" style={{ fontSize: 12 }}>Imobilizado por categoria</Text>}
                    dataSource={dados.ativo.imobilizado.categorias.slice(0, 6)}
                    columns={[
                      { title: 'Categoria', dataIndex: 'categoria', key: 'c', ellipsis: true },
                      { title: 'Qtd', dataIndex: 'qtd', key: 'q', align: 'right', width: 55 },
                      { title: 'Custo', dataIndex: 'custo', key: 'cu', align: 'right', width: 105, render: (v: number) => brl(v) },
                      { title: 'Valor atual', dataIndex: 'liquido', key: 'l', align: 'right', width: 105, render: (v: number) => brl(v) },
                    ]}
                  />
                </Card>
              </Col>

              {/* PASSIVO + PL */}
              <Col xs={24} lg={12} style={{ marginBottom: 20 }}>
                <Card size="small" title="PASSIVO e Patrimônio Líquido">
                  {linha(<>Contas a pagar ({dados.passivo.fornecedores.qtd}){dica(`Despesas já incorridas e não pagas (inclui ${brl(dados.info.cartao_em_aberto_incluso_no_cp)} de faturas de cartão).`)}</>,
                    dados.passivo.fornecedores.total, delta(dados.passivo.fornecedores.total, dados.comparativo_base.fornecedores))}
                  {linha(<>Empréstimos (principal){dica('Só o principal devido conta como dívida — juros futuros não. Posição atual das parcelas em aberto.')}</>,
                    dados.passivo.emprestimos.principal)}
                  {linha(<>Adiantamentos de clientes{dica('Dinheiro recebido antes do serviço (Corsan). Já está no resultado, mas ainda tem entrega vinculada — no balanço é obrigação, não riqueza.')}</>,
                    dados.passivo.adiantamentos.total, delta(dados.passivo.adiantamentos.total, dados.comparativo_base.adiantamentos))}
                  {linha('Total do Passivo', dados.passivo.total, undefined, true)}
                  {linha(<Text style={{ color: '#15803d' }}>Patrimônio Líquido (Ativo − Passivo)</Text>, dados.patrimonio_liquido, undefined, true)}

                  <Alert
                    type="info" showIcon style={{ marginTop: 12 }}
                    message="Futuro já contratado (fora do balanço)"
                    description={`A carteira a faturar soma ${brl(dados.info.carteira_a_faturar.liquido)} líquidos (${dados.info.carteira_a_faturar.qtd} parcelas) contra ${brl(dados.info.compromissos_futuros.total)} de compromissos futuros já agendados. O balanço só conta o que JÁ aconteceu — o futuro contratado é o motor que vai alimentar os próximos meses.`}
                  />
                </Card>
              </Col>
            </Row>

            {/* Ponte lucro → caixa */}
            <Card size="small" title={`Ponte lucro → caixa (${dados.base} → ${dados.mes})`} style={{ marginBottom: 20 }}>
              <Row gutter={24}>
                <Col xs={24} lg={14}>
                  <Table
                    size="small" pagination={false} rowKey="rotulo"
                    dataSource={[
                      ...dados.ponte.passos,
                      { rotulo: '= Variação esperada de caixa', valor: dados.ponte.variacao_esperada },
                      { rotulo: 'Variação real (bancos)', valor: dados.ponte.variacao_real },
                      { rotulo: 'Resíduo a investigar', valor: dados.ponte.residuo },
                    ]}
                    columns={[
                      {
                        title: 'Componente', dataIndex: 'rotulo', key: 'r',
                        render: (v: string) => <Text strong={v.startsWith('=') || v.startsWith('Variação real')}>{v}</Text>,
                      },
                      {
                        title: 'Efeito no caixa', dataIndex: 'valor', key: 'v', align: 'right', width: 140,
                        render: (v: number, r: any) => (
                          <Text strong={String(r.rotulo).startsWith('=') || String(r.rotulo).startsWith('Variação real')}
                            style={{ color: r.rotulo === 'Resíduo a investigar' ? '#b45309' : v >= 0 ? '#15803d' : '#dc2626' }}>
                            {v >= 0 ? '+' : ''}{brl(v)}
                          </Text>
                        ),
                      },
                    ]}
                  />
                </Col>
                <Col xs={24} lg={10}>
                  <Statistic title={`Caixa em ${dados.base}`} value={dados.ponte.caixa_inicial} precision={0} prefix="R$" />
                  <Statistic title={`Caixa em ${dados.mes}`} value={dados.ponte.caixa_final} precision={0} prefix="R$" style={{ marginTop: 12 }} />
                  <Alert
                    type="success" showIcon style={{ marginTop: 16 }}
                    message="Por que lucro não é caixa"
                    description="O lucro da DRE pode estar preso em contas a receber, ter virado equipamento, quitado dívidas antigas ou ser adiantamento reconhecido. Esta ponte mostra linha a linha pra onde o resultado foi — o que sobrar sem explicação aparece no resíduo."
                  />
                </Col>
              </Row>
            </Card>

            {/* Detalhes: contas bancárias + empréstimos + adiantamentos */}
            <Row gutter={24}>
              <Col xs={24} lg={10} style={{ marginBottom: 20 }}>
                <Card size="small" title="Onde o caixa está (por conta)">
                  <Table
                    size="small" pagination={false} rowKey={(r: any) => r.conta + r.saldo}
                    dataSource={dados.ativo.caixa.contas}
                    locale={{ emptyText: 'Snapshot sem detalhe por conta nesta data' }}
                    columns={[
                      { title: 'Conta', dataIndex: 'conta', key: 'c', ellipsis: true },
                      { title: 'Saldo', dataIndex: 'saldo', key: 's', align: 'right', width: 120, render: (v: number) => brl(v) },
                    ]}
                  />
                  {dados.ativo.caixa.contas.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Detalhe por conta do snapshot {dados.ativo.caixa.contas[0]?.data} — o total do card usa o snapshot mais recente disponível.
                    </Text>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={14} style={{ marginBottom: 20 }}>
                <Card size="small" title="Empréstimos e adiantamentos">
                  <Table
                    size="small" pagination={false} rowKey={(r: any) => r.instituicao + r.sistema}
                    dataSource={dados.passivo.emprestimos.detalhe}
                    locale={{ emptyText: 'Nenhum empréstimo ativo' }}
                    columns={[
                      { title: 'Instituição', dataIndex: 'instituicao', key: 'i', ellipsis: true },
                      { title: 'Sistema', dataIndex: 'sistema', key: 'si', width: 90, render: (v: string) => <Tag>{v}</Tag> },
                      { title: 'Parcelas', dataIndex: 'parcelas_abertas', key: 'p', align: 'right', width: 80 },
                      { title: 'Principal', dataIndex: 'principal', key: 'pr', align: 'right', width: 110, render: (v: number) => brl(v) },
                    ]}
                  />
                  <Table
                    size="small" pagination={false} style={{ marginTop: 12 }} rowKey={(r: any) => r.descricao + r.periodo}
                    title={() => <Text type="secondary" style={{ fontSize: 12 }}>Adiantamentos de clientes (a estornar quando as medições faturarem)</Text>}
                    dataSource={dados.passivo.adiantamentos.itens}
                    locale={{ emptyText: 'Nenhum adiantamento registrado' }}
                    columns={[
                      { title: 'Descrição', dataIndex: 'descricao', key: 'd', ellipsis: true },
                      { title: 'Período', dataIndex: 'periodo', key: 'p', width: 90 },
                      { title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 110, render: (v: number) => brl(v) },
                    ]}
                  />
                </Card>
              </Col>
            </Row>

            <Text type="secondary" style={{ fontSize: 11 }}>
              Balanço GERENCIAL montado dos dados do sistema (snapshots de saldo, CR/CP, patrimônio, empréstimos) — não substitui o
              balanço contábil oficial do contador (sem capital social, depreciação fiscal ou provisões trabalhistas).
              Contas a pagar = competência até o fim do mês da foto, não pagas até a data. Empréstimos sempre na posição atual.
            </Text>
          </>
        )}
      </Spin>
    </div>
  );
}
